import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FormBase } from 'src/app/_models/form-base';
import { ErrorObject } from 'src/app/_models/error';
import { ModalService } from 'src/app/_services/modal.service';
import { ButtonComponent } from '../../buttons/button/button.component';
import { MatIconButtonComponent } from '../../buttons/mat-icon-button/mat-icon-button.component';
import { UnderlinedFormFieldComponent } from '../../forms/underlined-form-field/underlined-form-field.component';
import { MatIcon } from '@angular/material/icon';
import { MatOutlinedButtonComponent } from '../../buttons/mat-outlined-button/mat-outlined-button.component';
import { Checkbox } from 'src/app/_models/checkbox';
import { CheckboxComponent } from '../../selection-controls/checkbox/checkbox.component';

@Component({
  selector: 'app-recovery-codes-modal',
  templateUrl: './recovery-codes-modal.component.html',
  styleUrl: './recovery-codes-modal.component.scss',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    UnderlinedFormFieldComponent,
    ButtonComponent,
    MatIconButtonComponent,
    MatIcon,
    MatOutlinedButtonComponent,
    CheckboxComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecoveryCodesModalComponent implements OnInit, OnDestroy {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly modalService = inject(ModalService);
  private readonly element: HTMLElement;

  id = input<string>();
  parentForm = input<FormGroup>();
  passwordField = input<FormBase<string>>();
  codeField = input<FormBase<string>>();
  showCodes = input<boolean>(false);
  recoveryCodes = input<string[]>([]);
  primaryButtonText = input<string>();
  primaryButtonId = input<string>();
  secondaryButtonText = input<string>();
  secondaryButtonId = input<string>();
  isProcessing = input<boolean>(false);
  error = input<ErrorObject | null>(null);

  modalButtonClicked = output<string>();

  clipboardError = signal<string | null>(null);

  readonly savedRecoveryCodes = new Checkbox(
    'savedRecoveryCodes',
    'I have saved my recovery codes in a secure location',
    'done',
  );

  readonly recoveryCodeForm = this.formBuilder.group({
    [this.savedRecoveryCodes.id]: [false, Validators.requiredTrue],
  });

  constructor() {
    this.element = this.elementRef.nativeElement;
  }

  ngOnInit(): void {
    this.element.style.display = 'none';
    this.element.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.className === 'modal--container' && !this.isProcessing()) {
        this.close();
      }
    });
    this.modalService.add(this);
  }

  open(): void {
    this.element.style.display = 'block';
  }

  close(): void {
    this.element.style.display = 'none';
  }

  copyRecoveryCodes(): void {
    const codes = this.recoveryCodes();
    if (!codes?.length) return;
    this.clipboardError.set(null);
    navigator.clipboard.writeText(codes.join('\n')).catch(() => {
      this.clipboardError.set('Failed to copy — please select and copy the codes manually.');
    });
  }

  buttonEventHandler(buttonId: string): void {
    this.modalButtonClicked.emit(buttonId);
  }

  ngOnDestroy(): void {
    this.modalService.remove(this.id());
    this.element.remove();
  }
}
